// core-data module operations (services/portal/docs/modules/core-data.md).
// One file per module (stack.md) — everything the six table pages + their
// sidebars read.
//
// LIST vs DETAIL split (ui-conventions.md #94, owner ruling 2026-08-22): the
// five public quasi-static collections (Tenants/Competitions/Events/Teams/
// Universities) are prefetched wholesale and cached client-side
// (src/stores/coreData.ts), so their LIST queries carry only what the tables
// + facets + entity links + full-text search need — never billing/contact/
// participations, which a cached-forever collection should not be shipping
// to every visitor on every page load. Full field coverage (#64) instead
// lives in a per-entity DETAIL query, fetched on demand when a sidebar
// opens. Persons has no LIST/DETAIL split: it's the one Relay
// connection (server-search-driven, #13/#14), unaffected by this change.
//
// Two of the five DETAIL queries deviate from a plain `<Entity>Detail(id)`
// shape because the schema itself has no by-id lookup for them:
//   - Tenant: there is no `tenant(id)` query at all (schema.graphqls' Query
//     type has only `tenants: [Tenant!]!`), and Tenant carries no gated or
//     heavy fields beyond what the LIST query already has (id, slug, name,
//     competitions → links) — so there is no separate TenantDetail
//     operation. The sidebar reads the entity straight out of the cached
//     `tenants` collection, which is strictly better than a network
//     round-trip for a field set that never grows.
//   - Event: there is no `event(id)` query — only
//     `event(competitionSlug, season, sequence)`. EventDetail is keyed by
//     that address instead; the sidebar already has all three values on the
//     list row it was opened from.
import { graphql } from 'src/graphql'

// ─────────────────────────── LIST (cached collections) ──────────────────

export const TenantsDocument = graphql(/* GraphQL */ `
  query Tenants {
    tenants {
      id
      slug
      name
      competitions {
        id
        slug
        name
      }
    }
  }
`)

export const CompetitionsDocument = graphql(/* GraphQL */ `
  query Competitions {
    competitions {
      id
      slug
      name
      tenant {
        id
        slug
        name
      }
      events {
        id
        season
        sequence
        name
      }
    }
  }
`)

export const EventsDocument = graphql(/* GraphQL */ `
  query Events {
    events {
      id
      season
      sequence
      name
      venue
      startsOn
      endsOn
      timezone
      classes {
        classes
        cups
        historic
        startNumberMin
        startNumberMax
      }
      competition {
        id
        slug
        name
        tenant {
          id
        }
      }
    }
  }
`)

export const TeamsDocument = graphql(/* GraphQL */ `
  # includeHidden is hardcoded true, not a variable: the store caches the
  # WHOLE collection once (#94), and "include hidden" is a client-side table
  # toggle (TeamsPage) rather than a reason to refetch.
  query Teams {
    teams(includeHidden: true) {
      id
      class
      name
      hidden
      # Just the reference (fleet-fix #118). The seven university fields the
      # tables read (name, nameShort, nameInternal, nameLocal, city, country
      # {iso2, nameEn}) are a verbatim copy of the universities collection
      # this same store already caches — ~30% of every team row, re-shipped
      # per team, in the portal's largest payload. src/stores/coreData.ts
      # joins them back in by id out of that collection, the way the Event
      # facet already labels off the cached events (#95).
      university {
        id
      }
      vehicles {
        id
        season
        vehicleName
        # Slim — just the event ref + status, no nested event fields (#95):
        # the Event facet (TeamsPage.vue) labels off the coreData events
        # collection instead of carrying "name (season)" through every
        # team's localStorage payload.
        # No participation id: nothing reading the cached collection ever
        # uses it (TeamsPage's Event/Status facet, VehiclesPage's rows and
        # lib/globalScopeRules all key off event.id + status), and at ~25k
        # participations the field alone is a six-figure byte count in a
        # payload that is already the portal's largest (fleet-fix #118).
        # The DETAIL query keeps it — that join really is by id.
        participations {
          status
          event {
            id
          }
        }
      }
    }
  }
`)

export const UniversitiesDocument = graphql(/* GraphQL */ `
  # includeHidden is hardcoded true — see the Teams query comment above.
  query Universities {
    universities(includeHidden: true) {
      id
      name
      nameShort
      namePits
      nameInternal
      nameLocal
      city
      country {
        iso2
        nameEn
      }
      hidden
      teams {
        id
      }
    }
  }
`)

// UniversitiesPage's "Include hidden" chip: the soft-deleted universities
// too (includeDeleted, honoured for the platform.admin / admin / read.all
// ceiling), so a deleted row shows muted with its pill and can be undeleted.
// Kept off the cached collection above, which every university picker reads.
export const UniversitiesWithDeletedDocument = graphql(/* GraphQL */ `
  query UniversitiesWithDeleted {
    universities(includeHidden: true, includeDeleted: true) {
      id
      name
      nameShort
      namePits
      nameInternal
      nameLocal
      city
      country {
        iso2
        nameEn
      }
      hidden
      deletedAt
      teams {
        id
      }
    }
  }
`)

// ─────────────────────────── DETAIL (on-demand, sidebar) ─────────────────
// Full field coverage per the module brief's schema audit (#64) — the
// sidebar is the future edit surface (services/portal/docs/modules/
// core-data.md), rendering read-only values for now.

/** A competition's details (#170 AN1): where it is held, its website, and
 *  its World Ranking List flags — the Details section reads and edits them. */
export const CompetitionDetailsFields = graphql(/* GraphQL */ `
  fragment CompetitionDetailsFields on Competition {
    country {
      iso2
      nameEn
    }
    website
    venueCity
    latitude
    longitude
    countsForWrl
    active
    sinceYear
  }
`)

export const CompetitionDetailDocument = graphql(/* GraphQL */ `
  query CompetitionDetail($id: ID!) {
    competition(id: $id) {
      id
      slug
      name
      ...CompetitionDetailsFields
      tenant {
        id
        slug
        name
      }
      events {
        id
        season
        sequence
        name
      }
    }
  }
`)

export const EventDetailDocument = graphql(/* GraphQL */ `
  query EventDetail($competitionSlug: String!, $season: Int!, $sequence: Int!) {
    event(competitionSlug: $competitionSlug, season: $season, sequence: $sequence) {
      id
      season
      sequence
      name
      venue
      startsOn
      endsOn
      timezone
      classes {
        classes
        cups
        historic
        startNumberMin
        startNumberMax
      }
      competition {
        id
        slug
        name
        tenant {
          id
          slug
          name
        }
      }
      # statuses: null keeps the admin event view showing every entry — the
      # edge now defaults to the teams that actually compete, which is what
      # every scoring surface reads, but entry administration needs the
      # withdrawn, waiting and no-show rows too.
      participations(statuses: null) {
        id
        status
        startNumber
        pit
        teamName
        cups
        noDriverless
        # EventClass has no "id" field on the schema (schema.graphqls) — the
        # participation-list grouping (EventDetailPanel.vue) keys off "classes"
        # (joined with "+") instead.
        eventClass {
          classes
          cups
          historic
          startNumberMin
          startNumberMax
        }
        vehicle {
          id
          season
          vehicleName
          chassisType
          hybrid
          team {
            id
            name
            class
            university {
              id
              name
              # nameInternal/nameShort/city (ui-conventions.md #98): what
              # src/core/teamDisplay.ts needs — EventDetailPanel's
              # participation entries render the team via that formatter,
              # never the raw name, outside the team view itself.
              nameShort
              nameInternal
              city
              country {
                iso2
                nameEn
              }
            }
          }
        }
      }
    }
  }
`)

export const TeamDetailDocument = graphql(/* GraphQL */ `
  query TeamDetail($id: ID!) {
    team(id: $id) {
      id
      class
      name
      email
      website
      description
      # The logo is a docstore document on the PUBLIC tier, so downloadUrl is a
      # permanent unsigned object-store URL and renders directly as an image.
      # createdBy is deliberately NOT selected (issue #42): docstore types it
      # non-null while core refuses principal resolution for public callers, so
      # selecting it null-bubbles and the router drops the whole selection.
      logo {
        id
        filename
        mime
        downloadUrl
      }
      socials
      hidden
      # Schema-coverage closure (#64, specs/core-admin-crud/SPEC.md §5a):
      # soft-delete lifecycle (deleteTeam/undeleteTeam) and the admin-gated
      # full roster (TeamMembershipDialog / TeamDetailPanel "Memberships").
      deletedAt
      memberships {
        id
        role
        validFrom
        validUntil
        alumnusDeclaredAt
        person {
          id
          givenName
          familyName
        }
      }
      university {
        id
        name
        nameShort
        namePits
        nameInternal
        nameLocal
        city
        cityNotInName
        country {
          iso2
          nameEn
          nameLocal
        }
        website
        hidden
      }
      billing {
        address {
          recipient
          line1
          line2
          line3
          postalArea
          city
          country
        }
        taxNumber
      }
      contact {
        address {
          recipient
          line1
          line2
          line3
          postalArea
          city
          country
        }
      }
      vehicles {
        id
        season
        vehicleName
        chassisType
        chassisReused
        hybrid
        # Schema-coverage closure (#64): free-text legacy fuel type,
        # editable via updateVehicle.
        fuelType
        participations {
          id
          status
          startNumber
          pit
          teamName
          cups
          noDriverless
          # Participation-list badges (owner ruling 2026-08-22, services/
          # portal/docs/modules/core-data.md "Participation lists") —
          # the participation card reads classes/noDriverless/cups/startNumber
          # off this selection, same as EventDetail's.
          eventClass {
            classes
          }
          event {
            id
            name
            season
            sequence
            competition {
              id
              slug
              name
            }
          }
        }
      }
    }
  }
`)

export const UniversityDetailDocument = graphql(/* GraphQL */ `
  query UniversityDetail($id: ID!) {
    university(id: $id) {
      id
      name
      nameShort
      namePits
      nameInternal
      nameLocal
      city
      cityNotInName
      country {
        iso2
        nameEn
        nameLocal
      }
      website
      hidden
      # Schema-coverage closure (#64, SPEC.md §5a): soft-delete lifecycle
      # (deleteUniversity/undeleteUniversity).
      deletedAt
      teams {
        id
        name
        class
        hidden
      }
    }
  }
`)

// ─────────────────────────── Persons ───────────────────────────────────────
// Server-driven search + pagination (#13/#14), no client facets (the server
// has no facet args on `persons` yet) — outside the scope of the coreData
// cache (#94 names five collections; persons isn't one of them). Adopted
// onto specs/global-scope-surfaces/SPEC.md's shipped surfaces (§5a/§5b/§5e,
// folded from collection-queries): `filter` is a clause union
// ([PersonSearchFilter!], OR across clauses), `orderBy` drives the table's
// server sort, `limit` is optional/uncapped (PersonsPage always sends one
// anyway — see its own PAGE_SIZE comment).

export const PersonsDocument = graphql(/* GraphQL */ `
  query Persons(
    $filter: [PersonSearchFilter!]
    $orderBy: [PersonOrder!]
    $offset: Int!
    $limit: Int!
    $includeDeleted: Boolean
    $withAccount: Boolean! = false
  ) {
    persons(filter: $filter, orderBy: $orderBy, offset: $offset, limit: $limit, includeDeleted: $includeDeleted) {
      items {
        id
        givenName
        familyName
        email
        scrubbedAt
        # The Account column (Linked / Unlinked). Identities resolve empty
        # without admin/read.all, so PersonsPage asks only for admins.
        identities @include(if: $withAccount) {
          issuer
        }
      }
      totalCount
    }
  }
`)

// S4 — accredited-in-scope (specs/global-scope-surfaces/SPEC.md §5c), fired
// by PersonsPage.vue whenever the shell-level global scope is active
// (ui-conventions.md #97): the whole "who is accredited at these events"
// view is ONE request against `Event.accreditations`. `states: [APPROVED]` is
// the spec's own worked example for "accredited-in-scope" — not every
// accreditation state counts, only the decided-and-accepted one.
//
// Unchanged by the accreditation collapse (issue #47) on purpose: a roster
// designation lands CONFIRMED, not APPROVED (A5), so widening the state list
// is what would fold every team member at the event into this population —
// a different question from the one this surface asks.
//
// Cached per its own variables (scopeEventIds) by urql's normal cache
// exchange — no bespoke memoization needed, since the same scope signature
// produces the same variables object content on every re-render.
export const ScopeAccreditedPersonsDocument = graphql(/* GraphQL */ `
  query ScopeAccreditedPersons($scopeEventIds: [ID!]!) {
    events(filter: [{ ids: $scopeEventIds }]) {
      id
      accreditations(states: [APPROVED]) {
        person {
          id
        }
      }
    }
  }
`)

export const PersonDetailDocument = graphql(/* GraphQL */ `
  query PersonDetail($id: ID!) {
    person(id: $id) {
      id
      givenName
      familyName
      email
      scrubbedAt
      # Schema-coverage closure (#64, SPEC.md §5a): PII, same read gate as
      # email — editable via updatePerson.
      nationality {
        iso2
        nameEn
      }
      # Profile satellite (SPEC.md §5c) — editable via memberAdminUpdateProfile,
      # a separate mutation from updatePerson (build item 7). A subset of
      # MemberProfile's fields: spokenLanguages/firstAid (JSON) and avatar
      # (docstore-owned) are left out of this editing pass — no structured
      # editor was specced for them here (#62 applies to socials specifically;
      # revisit if the portal needs to edit those two).
      profile {
        dateOfBirth
        mobile
        company
        education
        clothingSize
        department
        studies
        teamRoleTitle
        drivingLicenses
        isFirefighter
        firefighterPosition
        commWhatsapp
        commSignal
        fsgExperience
      }
    }
  }
`)

// ─────────────────────────── Mutations (core-admin-crud) ──────────────────
// specs/core-admin-crud/SPEC.md §5b/§5c. Every mutation carries a
// client-minted idempotencyKey (crypto.randomUUID(), see
// src/modules/core/lib/afterMutation.ts callers). Names/args verified
// against graph/client-schema.graphql — nothing here is skipped: every
// mutation SPEC.md §5b/§5c lists is present on the composed schema.

export const CreateTenantDocument = graphql(/* GraphQL */ `
  mutation CreateTenant($input: TenantInput!, $idempotencyKey: String) {
    createTenant(input: $input, idempotencyKey: $idempotencyKey) {
      id
      slug
      name
    }
  }
`)

export const UpdateTenantDocument = graphql(/* GraphQL */ `
  mutation UpdateTenant($id: ID!, $patch: TenantPatch!, $idempotencyKey: String) {
    updateTenant(id: $id, patch: $patch, idempotencyKey: $idempotencyKey) {
      id
      slug
      name
    }
  }
`)

export const CreateCompetitionDocument = graphql(/* GraphQL */ `
  mutation CreateCompetition($input: CompetitionInput!, $idempotencyKey: String) {
    createCompetition(input: $input, idempotencyKey: $idempotencyKey) {
      id
      slug
      name
      tenant {
        id
      }
    }
  }
`)

export const UpdateCompetitionDocument = graphql(/* GraphQL */ `
  mutation UpdateCompetition($id: ID!, $patch: CompetitionPatch!, $idempotencyKey: String) {
    updateCompetition(id: $id, patch: $patch, idempotencyKey: $idempotencyKey) {
      id
      slug
      name
      ...CompetitionDetailsFields
      tenant {
        id
      }
    }
  }
`)

/** The countries core knows (ISO 3166-1), for the competition's country
 *  picker (#170). Name order; a couple of hundred rows, fetched when the
 *  Details section opens for editing. */
export const CoreCountriesDocument = graphql(/* GraphQL */ `
  query CoreCountries {
    countries(orderBy: [{ field: NAME_EN }]) {
      iso2
      nameEn
    }
  }
`)

export const CreateEventDocument = graphql(/* GraphQL */ `
  mutation CreateEvent($input: EventInput!, $idempotencyKey: String) {
    createEvent(input: $input, idempotencyKey: $idempotencyKey) {
      id
      season
      sequence
      name
      competition {
        id
        slug
      }
    }
  }
`)

export const UpdateEventDocument = graphql(/* GraphQL */ `
  mutation UpdateEvent($id: ID!, $patch: EventPatch!, $idempotencyKey: String) {
    updateEvent(id: $id, patch: $patch, idempotencyKey: $idempotencyKey) {
      id
      name
      venue
      startsOn
      endsOn
      timezone
    }
  }
`)

// Wired per the mutation-surface audit (build item 2) — no editing UI calls
// this yet. Replacing an event's class-group set (add/remove/resize rows,
// matched by id, with the server's #63 child-block on a group still
// referenced by participations) is its own small editor; out of scope for
// this pass. EventDetailPanel's Classes section stays read-only.
export const SetEventClassesDocument = graphql(/* GraphQL */ `
  mutation SetEventClasses($eventId: ID!, $classes: [EventClassInput!]!, $idempotencyKey: String) {
    setEventClasses(eventId: $eventId, classes: $classes, idempotencyKey: $idempotencyKey) {
      id
      classes {
        classes
        cups
        historic
        startNumberMin
        startNumberMax
      }
    }
  }
`)

export const CreateTeamDocument = graphql(/* GraphQL */ `
  mutation CreateTeam($input: TeamInput!, $idempotencyKey: String) {
    createTeam(input: $input, idempotencyKey: $idempotencyKey) {
      id
      name
      class
      university {
        id
      }
    }
  }
`)

export const UpdateTeamDocument = graphql(/* GraphQL */ `
  mutation UpdateTeam($id: ID!, $patch: TeamPatch!, $idempotencyKey: String) {
    updateTeam(id: $id, patch: $patch, idempotencyKey: $idempotencyKey) {
      id
      university {
        id
      }
      name
      email
      website
      description
      socials
      hidden
      billing {
        address {
          recipient
          line1
          line2
          line3
          postalArea
          city
          country
        }
        taxNumber
      }
      contact {
        address {
          recipient
          line1
          line2
          line3
          postalArea
          city
          country
        }
      }
    }
  }
`)

export const DeleteTeamDocument = graphql(/* GraphQL */ `
  mutation DeleteTeam($id: ID!, $idempotencyKey: String) {
    deleteTeam(id: $id, idempotencyKey: $idempotencyKey) {
      id
      deletedAt
    }
  }
`)

export const UndeleteTeamDocument = graphql(/* GraphQL */ `
  mutation UndeleteTeam($id: ID!, $idempotencyKey: String) {
    undeleteTeam(id: $id, idempotencyKey: $idempotencyKey) {
      id
      deletedAt
    }
  }
`)

export const CreateUniversityDocument = graphql(/* GraphQL */ `
  mutation CreateUniversity($input: UniversityInput!, $idempotencyKey: String) {
    createUniversity(input: $input, idempotencyKey: $idempotencyKey) {
      id
      name
    }
  }
`)

export const UpdateUniversityDocument = graphql(/* GraphQL */ `
  mutation UpdateUniversity($id: ID!, $patch: UniversityPatch!, $idempotencyKey: String) {
    updateUniversity(id: $id, patch: $patch, idempotencyKey: $idempotencyKey) {
      id
      name
      nameShort
      namePits
      nameInternal
      nameLocal
      city
      cityNotInName
      country {
        iso2
      }
      website
      latitude
      longitude
      wikidataId
      hidden
    }
  }
`)

export const DeleteUniversityDocument = graphql(/* GraphQL */ `
  mutation DeleteUniversity($id: ID!, $idempotencyKey: String) {
    deleteUniversity(id: $id, idempotencyKey: $idempotencyKey) {
      id
      deletedAt
    }
  }
`)

// University merge (CO-18, fsgadmin MergeTools): the preview the dialog shows
// and the merge it confirms. The preview's collisions are exactly the merge's
// refusal, so a non-empty list keeps the confirm button disabled.
export const UniversityMergePreviewDocument = graphql(/* GraphQL */ `
  query UniversityMergePreview($fromId: ID!, $toId: ID!) {
    universityMergePreview(fromId: $fromId, toId: $toId) {
      teamsMoved
      fromTeams {
        id
        name
        class
        deletedAt
      }
      toTeams {
        id
        name
        class
        deletedAt
      }
      collisions {
        class
        fromTeam {
          id
          name
        }
        toTeam {
          id
          name
        }
      }
    }
  }
`)

export const MergeUniversitiesDocument = graphql(/* GraphQL */ `
  mutation MergeUniversities($fromId: ID!, $toId: ID!, $idempotencyKey: String) {
    mergeUniversities(fromId: $fromId, toId: $toId, idempotencyKey: $idempotencyKey) {
      id
      name
    }
  }
`)

export const UndeleteUniversityDocument = graphql(/* GraphQL */ `
  mutation UndeleteUniversity($id: ID!, $idempotencyKey: String) {
    undeleteUniversity(id: $id, idempotencyKey: $idempotencyKey) {
      id
      deletedAt
    }
  }
`)

export const CreatePersonDocument = graphql(/* GraphQL */ `
  mutation CreatePerson($input: PersonInput!, $idempotencyKey: String) {
    createPerson(input: $input, idempotencyKey: $idempotencyKey) {
      id
      givenName
      familyName
    }
  }
`)

export const UpdatePersonDocument = graphql(/* GraphQL */ `
  mutation UpdatePerson($id: ID!, $patch: PersonPatch!, $idempotencyKey: String) {
    updatePerson(id: $id, patch: $patch, idempotencyKey: $idempotencyKey) {
      id
      givenName
      familyName
      email
      nationality {
        iso2
      }
    }
  }
`)

// Wired per the mutation-surface audit (build item 2) — Event/Competition
// billing/shipping addresses (the only places a standalone Address is
// referenced today) have no editing UI in this pass (EventDetailPanel's fee
// block wasn't added to the DETAIL query either, same deferral). Kept here
// so a future address-picker has a ready mutation.
export const CreateAddressDocument = graphql(/* GraphQL */ `
  mutation CreateAddress($input: AddressInput!, $idempotencyKey: String) {
    createAddress(input: $input, idempotencyKey: $idempotencyKey) {
      id
    }
  }
`)

export const UpdateAddressDocument = graphql(/* GraphQL */ `
  mutation UpdateAddress($id: ID!, $patch: AddressPatch!, $idempotencyKey: String) {
    updateAddress(id: $id, patch: $patch, idempotencyKey: $idempotencyKey) {
      id
    }
  }
`)

export const CreateVehicleDocument = graphql(/* GraphQL */ `
  mutation CreateVehicle($input: VehicleInput!, $idempotencyKey: String) {
    createVehicle(input: $input, idempotencyKey: $idempotencyKey) {
      id
      season
      team {
        id
      }
    }
  }
`)

export const UpdateVehicleDocument = graphql(/* GraphQL */ `
  mutation UpdateVehicle($id: ID!, $patch: VehiclePatch!, $idempotencyKey: String) {
    updateVehicle(id: $id, patch: $patch, idempotencyKey: $idempotencyKey) {
      id
      vehicleName
      chassisType
      chassisReused
      hybrid
      fuelType
    }
  }
`)

export const CreateParticipationDocument = graphql(/* GraphQL */ `
  mutation CreateParticipation($input: ParticipationInput!, $idempotencyKey: String) {
    createParticipation(input: $input, idempotencyKey: $idempotencyKey) {
      id
      vehicle {
        id
      }
      event {
        id
      }
    }
  }
`)

export const UpdateParticipationDocument = graphql(/* GraphQL */ `
  mutation UpdateParticipation($id: ID!, $patch: ParticipationPatch!, $idempotencyKey: String) {
    updateParticipation(id: $id, patch: $patch, idempotencyKey: $idempotencyKey) {
      id
      status
      startNumber
      pit
      teamName
      noDriverless
      cups
    }
  }
`)

// Soft delete, reserved for an entry that should never have existed (issue
// #37 §5). Refused once anything hangs off the entry — the refusal names the
// blocker, and that message is what the dialog shows. A participation that
// really took place and then ended is a STATUS transition (WITHDRAWN /
// DEREGISTERED / NO_SHOW) through UpdateParticipation, never this.
export const DeleteParticipationDocument = graphql(/* GraphQL */ `
  mutation DeleteParticipation($id: ID!, $idempotencyKey: String) {
    deleteParticipation(id: $id, idempotencyKey: $idempotencyKey)
  }
`)

export const CreateTeamMembershipDocument = graphql(/* GraphQL */ `
  mutation CreateTeamMembership($input: TeamMembershipInput!, $idempotencyKey: String) {
    createTeamMembership(input: $input, idempotencyKey: $idempotencyKey) {
      id
      role
      validFrom
      validUntil
      person {
        id
        givenName
        familyName
      }
    }
  }
`)

export const UpdateTeamMembershipDocument = graphql(/* GraphQL */ `
  mutation UpdateTeamMembership($id: ID!, $patch: TeamMembershipPatch!, $idempotencyKey: String) {
    updateTeamMembership(id: $id, patch: $patch, idempotencyKey: $idempotencyKey) {
      id
      role
      validFrom
      validUntil
      alumnusDeclaredAt
    }
  }
`)

// ─────────────────────────── Vehicles/season + members (owner directive
// 2026-08-22: "vehicle/season based screen with jump to participating
// members and their details and everything else surfaced by the member
// service") ─────────────────────────────────────────────────────────────
//
// VehiclesPage has no LIST query of its own: the table is the cached Teams
// collection's vehicles flattened client-side (coreData store, #94) — a
// season/vehicleName/class/team-country row set the Teams LIST query
// already carries in full. VehicleDetailPanel's vehicle FACTS likewise ride
// the existing TeamDetail DETAIL query (unmodified, per this file's own
// rule): the schema has no `vehicle(id)` lookup (graph/client-schema.graphql
// Query type), so the panel resolves its vehicle by id out of
// TeamDetail(id: <owning team>).vehicles — the fallback this file's module
// comment anticipates for entities with no by-id query.
//
// What TeamDetail does NOT carry is each participation's roster
// (Participation.accreditations: [MemberTeamAccreditation!]!) —
// TeamDetailDocument is an existing operation this file must not modify. The
// schema also has no `participation(id)` lookup. The cheapest REAL path is
// the top-level `participations(filter: ParticipationFilter)` query:
// ParticipationFilter has `team`/`season` (no `id`), but a Vehicle is "one
// vehicle per team per season" (Vehicle type docstring) — so
// `participations(filter: {team, season})` returns EXACTLY the selected
// vehicle's own participations, with `accreditations` selected directly.
// Joined back onto TeamDetail's participations by `id` in
// VehicleDetailPanel.vue.
//
// The roster slot is an ACCREDITATION row now (member→core merge, issue #47
// A1/A3): the old `ParticipationMember` collapsed into the kind-discriminated
// accreditation spine, so REGULAR/LATE reads as `slotKind` and the row
// carries the full seven-state machine in `state`. `state` is selected
// because a removed member is WITHDRAWN in place rather than deleted (A5) —
// without it a retired slot would render as a live one.
export const ParticipationMembersDocument = graphql(/* GraphQL */ `
  query ParticipationMembers($team: ID!, $season: Int!) {
    participations(filter: { teams: [$team], seasons: [$season] }) {
      id
      accreditations {
        id
        person {
          id
          givenName
          familyName
        }
        slotKind
        state
        eso
        asr
        paymentStatus
        designatedAt
        selections
        eligibility {
          selections
          teamMemberBadge
          ticket
          requirements {
            type {
              slug
              name
              requiresReview
              versionBased
              isPii
            }
            requiredFor
            satisfied
            blocksPrint
          }
        }
        ticket
      }
    }
  }
`)

// PersonDetailPanel's member-service sections (Qualifications, Accreditations,
// and the MemberProfile fields PersonDetailDocument — an existing operation
// this file must not modify — doesn't select). A separate on-demand query,
// fired by the panel itself once `person.id` is known (#64 full field
// coverage on MemberQualification*/MemberAccreditation*, read completely per
// the module brief). qualificationRecords (not the derived `qualifications`
// standings alone) is selected for full holder+revisions+reviews coverage;
// accreditations carries the FOUR concrete kinds via inline fragments
// (interface MemberAccreditation) and is fetched unfiltered — Accreditations
// is filterable CLIENT-side (kind/state) per the build brief.
//
// TEAM_MEMBER is the fourth kind since the member→core merge (issue #47,
// A21): `Person.designation`/`.designations` — the separate roster projection
// this panel never queried — were retired into the same accreditation plane,
// so `accreditations` now also returns the person's roster designations and
// the panel renders their slot facts through MemberTeamAccreditation.
export const PersonMemberDetailDocument = graphql(/* GraphQL */ `
  query PersonMemberDetail($id: ID!) {
    person(id: $id) {
      id
      # The picture is docstore's Person.profilePicture, not core's
      # profile.avatar: a legacy ref no upload sets (j118).
      profilePicture {
        id
        filename
        mime
        size
        status
        downloadUrl
      }
      profile {
        spokenLanguages
        firstAid
        tosAcceptedAt
        nameFrozenAt
      }
      qualificationRecords {
        id
        type {
          slug
          name
          requiresReview
          versionBased
          isPii
        }
        standing {
          valid
          basisVersion
          state
          reviewedAt
          validUntil
        }
        revisions {
          n
          submittedAt
          supersededAt
          # Full blob-source coverage (ui-report 01a040a9): mime/size/
          # downloadUrl feed the in-view preview (src/core/useDocumentBlob.ts)
          # instead of rendering the filename as dead text. The document field
          # itself resolves NULL for callers the member subgraph PII-gates —
          # the panel renders nothing then.
          document {
            id
            filename
            mime
            size
            status
            downloadUrl
          }
        }
        reviews {
          verdict
          basisVersion
          comment
          reviewedAt
          revision
          reviewedBy {
            id
            displayName
          }
          correctionRequestedAt
          correctionResolvedAt
          correctionAdequate
          frozenFirstName
          frozenLastName
        }
      }
      accreditations {
        __typename
        id
        event {
          id
          name
          season
        }
        kind
        state
        appliedAt
        decidedAt
        reviewComment
        checkedInAt
        withdrawnAt
        ... on MemberTeamAccreditation {
          # The roster slot the designation IS: which entry, which slot, and
          # the fee/selection facts that hang off it. teamName/vehicle.team
          # feed the entry link — the person panel is outside the team view,
          # so the entry has to name itself.
          slotKind
          eso
          asr
          paymentStatus
          designatedAt
          selections
          participation {
            id
            teamName
            vehicle {
              id
              team {
                id
                name
              }
            }
          }
        }
        ... on MemberVolunteerAccreditation {
          preferredJob
          # The catalogue entry behind preferredJob (issue #23) — the panel
          # shows the job's NAME. Null when nothing is set or the stored id
          # names no catalogue row, which is when the raw code below shows.
          preferredJobRef {
            id
            name
          }
          assignedJob
          roleCode
          isTrackmarshall
          isDriver
          helpsDismantling
          partner { id givenName familyName }
          arrival
          departure
          accommodation {
            date
            choice
          }
          roommatePref {
            id
            givenName
            familyName
          }
          clothing {
            shirtType
            gloveSize
            pantsSize
            pantsCount
            poloCount
            safetyShoeSize
          }
          meal {
            vegetarian
            dinner
            other
          }
          additionalSkills
          motivation
          comment
          contactToOrga
          orgaComment
          orgaStatus
        }
        ... on MemberJudgeAccreditation {
          discipline
          seniority
          scoringCategory
          # university (ui-conventions.md #98): teamDisplay() input for
          # PersonDetailPanel's "Conflict teams" — outside the team view.
          # NOTE (mock only): src/mock/member.ts's teamRef() builder returns
          # {id, name} only, no university — this file must not edit that
          # module. teamDisplay() degrades gracefully to the plain name when
          # university is absent (its own fallback tiers), so mock mode
          # renders unformatted here until member.ts's fixture catches up; a
          # real server (which always resolves University! as non-null)
          # renders the formatted string correctly.
          conflictTeams {
            id
            name
            university {
              name
              nameShort
              nameInternal
              city
              country {
                iso2
              }
            }
          }
          scheduleNotes
          availableDays
        }
        ... on MemberPressAccreditation {
          outlet
          medium
          pressFunction
          affiliation
          eventDays
          accreditationDoc {
            id
            filename
            mime
            size
            status
            downloadUrl
          }
          pressPass {
            id
            filename
            mime
            size
            status
            downloadUrl
          }
          pressPassIssuedAt
        }
      }
    }
  }
`)

// Member subgraph (SPEC.md §5c) — a SEPARATE mutation call from updatePerson
// (build item 7), never batched into one document: the two calls surface
// their errors independently.
export const MemberAdminUpdateProfileDocument = graphql(/* GraphQL */ `
  mutation MemberAdminUpdateProfile($personId: ID!, $patch: MemberProfileAdminPatch!, $idempotencyKey: String) {
    memberAdminUpdateProfile(personId: $personId, patch: $patch, idempotencyKey: $idempotencyKey) {
      dateOfBirth
      mobile
      company
      education
      clothingSize
      department
      studies
      teamRoleTitle
      drivingLicenses
      isFirefighter
      firefighterPosition
      commWhatsapp
      commSignal
      fsgExperience
    }
  }
`)

// The person panel's in-view qualification review (ui-report 01a040a9, owner
// scope ruling: HIC and every reviewable qualification review from the
// document preview dialog). qual.review-gated server-side (member SDL); the
// server always reviews the CURRENT head revision. The response is minimal —
// the panel re-resolves through afterMutationSuccess's live bump, never from
// this payload.
export const MemberReviewQualificationDocument = graphql(/* GraphQL */ `
  mutation MemberReviewQualification(
    $id: ID!
    $verdict: MemberReviewVerdict!
    $basisVersion: String
    $comment: String
    $idempotencyKey: String
  ) {
    memberReviewQualification(
      id: $id
      verdict: $verdict
      basisVersion: $basisVersion
      comment: $comment
      idempotencyKey: $idempotencyKey
    ) {
      verdict
      comment
      reviewedAt
      revision
    }
  }
`)

// The admin attestation (issue #36 register Q): an event or platform admin
// records a verdict on a qualification with NO document behind it, setting the
// coverage end inline. A separate mutation from memberReviewQualification so an
// un-evidenced pass stays distinguishable (the review row carries
// attested=true). The panel re-resolves through afterMutationSuccess's live
// bump, so the payload is minimal.
export const MemberReviewQualificationRecordDocument = graphql(/* GraphQL */ `
  mutation MemberReviewQualificationRecord(
    $qualificationId: ID!
    $verdict: MemberReviewVerdict!
    $validUntil: Date
    $comment: String
    $idempotencyKey: String
  ) {
    memberReviewQualificationRecord(
      qualificationId: $qualificationId
      verdict: $verdict
      validUntil: $validUntil
      comment: $comment
      idempotencyKey: $idempotencyKey
    ) {
      id
      standing {
        state
        valid
        validUntil
      }
    }
  }
`)

// ── Account / identity linking (legacy-binding §5, support-mediated claim) ────

// Admin-only context for the person panel's Account section: the handle and
// the identity links tell the operator whether a community account already
// exists before minting a support ticket.
export const PersonAccountStatusDocument = graphql(/* GraphQL */ `
  query PersonAccountStatus($id: ID!) {
    person(id: $id) {
      id
      username
      identities {
        issuer
        evidence
        createdAt
      }
    }
  }
`)

// Support-mediated claim (system.all): mints a link ticket whose setupUrl the
// operator delivers out-of-band; redemption at /setup?ticket= creates and
// links the community account with evidence "support".
export const AdminIssueClaimTicketDocument = graphql(/* GraphQL */ `
  mutation AdminIssueClaimTicket($personId: ID!, $idempotencyKey: String!) {
    adminIssueClaimTicket(personId: $personId, idempotencyKey: $idempotencyKey) {
      setupUrl
      expiresAt
    }
  }
`)

// ─────────────────────── deleted-participation restore (issue #57) ──────────
// Root Query.participations(includeDeleted: true) is the only surface that
// returns soft-deleted rows (nested vehicle.participations and
// event.participations drop tombstones unconditionally). Scoped to one team
// + season pair so the result set is bounded and meaningful to the viewer.
export const DeletedParticipationsForTeamDocument = graphql(/* GraphQL */ `
  query DeletedParticipationsForTeam($teamId: ID!, $season: Int!) {
    participations(filter: [{ teams: [$teamId], seasons: [$season] }], includeDeleted: true) {
      id
      # "deleted <date>" in the Vehicles panel's disclosure; null on live rows.
      deletedAt
      vehicle {
        id
      }
      event {
        id
        name
      }
      status
      startNumber
      pit
      teamName
      noDriverless
      cups
      eventClass {
        classes
      }
    }
  }
`)

/** Restore a soft-deleted participation. Gated to the tombstone's admin
 *  audience by the server (competition.admin or platform.admin ceiling). */
export const UndeleteParticipationDocument = graphql(/* GraphQL */ `
  mutation UndeleteParticipation($id: ID!, $idempotencyKey: String) {
    undeleteParticipation(id: $id, idempotencyKey: $idempotencyKey) {
      id
      vehicle {
        id
      }
      event {
        id
      }
    }
  }
`)

// The Vehicles table's per-entry decoration (prototype `tool-vehicles`): the
// car number and the cups of every entry at the events in view. The cached
// Teams collection carries only status + event per participation (fleet-fix
// #118 keeps it slim), so the start number and the Driverless Cup flag come
// from this one query, bounded by the events the global scope (or the
// current season without one) puts on screen — never the whole history.
export const VehicleEntriesDocument = graphql(/* GraphQL */ `
  query VehicleEntries($events: [ID!]!) {
    participations(filter: [{ events: $events }]) {
      id
      startNumber
      cups
      vehicle {
        id
      }
      event {
        id
      }
    }
  }
`)

// The Vehicles panel's "Media specs" section: whether the specs are public,
// whether any were submitted (null specs = nothing captured, or not visible
// to this viewer), and the approved car image. Per team — the schema has no
// vehicle(id) lookup — so the open vehicle is picked out of its team's.
export const VehicleMediaDocument = graphql(/* GraphQL */ `
  query VehicleMedia($team: ID!) {
    team(id: $team) {
      id
      vehicles {
        id
        mediaSpecsPublic
        mediaSpecs
        mediaImageDocument {
          id
          filename
          downloadUrl
        }
      }
    }
  }
`)

// The member-documents reviewer queue (j55 DL-4 + CO-16, MemberDocumentsPage).
// The row query carries what the row, the team group header and the review
// dialogs read; the counts query is the same population without the show
// filter, so the per-team status counts stay complete (TYPO3's status_count).
export const MemberQualificationQueueDocument = graphql(/* GraphQL */ `
  query MemberQualificationQueue($filter: [MemberQualificationQueueFilter!]) {
    memberQualificationQueue(filter: $filter) {
      holderHicState
      holderHicValidAtEvent
      participation {
        id
        startNumber
        teamName
        vehicle {
          id
          team {
            id
            name
          }
        }
      }
      qualification {
        id
        person {
          id
          givenName
          familyName
        }
        type {
          slug
          name
          requiresReview
          versionBased
          isPii
        }
        standing {
          valid
          state
          reviewedAt
          validUntil
        }
        revisions {
          n
          submittedAt
          supersededAt
          document {
            id
            filename
            mime
            size
            status
            downloadUrl
          }
        }
      }
    }
  }
`)

export const MemberQualificationQueueCountsDocument = graphql(/* GraphQL */ `
  query MemberQualificationQueueCounts($filter: [MemberQualificationQueueFilter!]) {
    memberQualificationQueue(filter: $filter) {
      participation {
        id
        startNumber
        teamName
        vehicle {
          id
          team {
            id
            name
          }
        }
      }
      qualification {
        id
        standing {
          state
        }
      }
    }
  }
`)

// ─────────────────────── event participation roster (CO-14) ─────────────────
// TeamsPage's participation view (components/EventParticipationsTable.vue):
// every entry of ONE event, whatever its status — the root query with no
// `statuses` clause returns them all, where Event.participations would
// default to PARTICIPATING only. Status/class narrowing happens client-side
// through the page's facets.
//
// Captains come from `members(includeHistory: true)`: the importer brings
// TYPO3's standby captains in as CAPTAIN memberships, so "the captains" are
// every active CAPTAIN row, and the ended ones feed the last-two-seasons
// toggle. `email` and `profile.mobile` are PII the server answers with null
// for a viewer without read.pii — the table renders what arrives and never
// infers anything from it. `accreditations` carries the ESO/ASR flags for the
// footer counts.
export const EventParticipationRosterDocument = graphql(/* GraphQL */ `
  query EventParticipationRoster($eventId: ID!) {
    participations(filter: [{ events: [$eventId] }]) {
      id
      status
      startNumber
      pit
      teamName
      noDriverless
      cups
      event {
        id
        season
      }
      vehicle {
        id
        team {
          id
          name
          class
          # The Team cell's logo (prototype tool-teams event mode).
          logo {
            id
            downloadUrl
          }
          university {
            id
            name
            country {
              iso2
              nameEn
            }
          }
          members(includeHistory: true) {
            id
            role
            validFrom
            validUntil
            person {
              id
              givenName
              familyName
              email
              profile {
                mobile
              }
            }
          }
          contact {
            address {
              id
              recipient
              email
              phone
            }
          }
        }
      }
      accreditations {
        id
        state
        eso
        asr
      }
    }
  }
`)

// ─────────────────────────── Teams list: the current season (T4) ───────────
// TeamsPage's list columns that live on a PARTICIPATION — car number, pit,
// cups (the Competition tag and the Driverless Cup chip) — plus the team logo,
// for the in-scope events of ONE season (the latest the global scope reaches).
// Deliberately not on the cached Teams collection: that payload is the
// portal's largest (fleet-fix #118) and spans every season there is, while
// these columns only ever describe the season on screen — a handful of
// events, bounded by the scope.
export const TeamsScopeParticipationsDocument = graphql(/* GraphQL */ `
  query TeamsScopeParticipations($eventIds: [ID!]!) {
    participations(filter: [{ events: $eventIds }]) {
      id
      status
      startNumber
      pit
      cups
      event {
        id
      }
      vehicle {
        id
        team {
          id
          logo {
            id
            downloadUrl
          }
        }
      }
    }
  }
`)

// ─────────────────────────── event participant roster (CO-15) ───────────────
// RosterPage: every accreditation of ONE event, any kind and any state — the
// facets narrow client-side, so the row count for an event is its number of
// accreditations. `Event.accreditations` is the interface; the team entry,
// slot and HIC/ESO/ASR standing exist only on MemberTeamAccreditation. The
// standing is the server's eligibility as it stands (valid on the last event
// day since D-11); the page only reads it.
//
// PII: `email`, `nationality` and `profile.dateOfBirth` resolve to null for a
// viewer without the grant, and the cells stay empty.
//
// "Printed" is not on the accreditation (`ticket` there is the eligibility
// state NONE/DISPLAYED/PRINTABLE). It comes from the event's printed tickets in
// the same request, matched on (person, ticket type's accreditation kind).
// Event.tickets is filtered per row by the ticket service, so a viewer without
// ticket read access gets [] and every row reads as not printed.
//
// The volunteer roster (Event office, owner ruling 2026-10-18) reads the same
// answer: the volunteer arm's job (the catalogue entry behind preferredJob,
// with its badge colours), role, assigned job and accommodation nights, and
// the event's night window (`volunteerNights`) the requested days are drawn
// against.
export const EventRosterDocument = graphql(/* GraphQL */ `
  query EventRoster($eventId: ID!) {
    events(filter: { ids: [$eventId] }) {
      id
      volunteerNights
      accreditations {
        __typename
        id
        kind
        state
        appliedAt
        reviewComment
        person {
          id
          givenName
          familyName
          email
          nationality {
            iso2
            nameEn
          }
          profile {
            dateOfBirth
            clothingSize
          }
        }
        ... on MemberTeamAccreditation {
          slotKind
          selections
          eligibility {
            requirements {
              type {
                slug
              }
              requiredFor
              satisfied
            }
          }
          participation {
            id
            startNumber
            cups
            vehicle {
              id
              team {
                id
                name
                class
                university {
                  id
                  name
                  nameShort
                  nameInternal
                  city
                  country {
                    iso2
                    nameEn
                  }
                }
              }
            }
          }
        }
        ... on MemberVolunteerAccreditation {
          # docstore's picture for the volunteer roster's avatars; null when
          # there is none or the caller may not see it. Volunteers only: the
          # docstore resolves it per person.
          person {
            id
            profilePicture {
              id
              downloadUrl
            }
          }
          preferredJob
          preferredJobRef {
            id
            name
            nameShort
            colorBg
            colorText
          }
          roleCode
          accommodation {
            date
            choice
          }
        }
      }
      tickets(filter: [{ printed: true, hasPerson: true }]) {
        id
        printedAt
        person {
          id
        }
        type {
          id
          accredKind
        }
      }
    }
  }
`)

/** A reviewer's decision on one accreditation (accred.review) — the roster's
 *  volunteer decisions (redesign batch 6). The lifecycle is core's
 *  (services/core/internal/lifecycle/accreditation.go): approve, decline or
 *  start review; there is no waiting-list state. */
export const AccreditationReviewDocument = graphql(/* GraphQL */ `
  mutation AccreditationReview(
    $id: ID!
    $decision: MemberAccreditationDecision!
    $comment: String
    $idempotencyKey: String
  ) {
    memberReviewAccreditation(
      id: $id
      decision: $decision
      comment: $comment
      idempotencyKey: $idempotencyKey
    ) {
      id
      state
    }
  }
`)

// ─────────────────────────── Participations (issue #138 F1/F2/F5) ───────────
// Core › Participations: the participation as a record of its own. The LIST
// is every entry at the events the global scope leaves on screen (#97) — one
// query bounded by those events, never a client join over the Teams cache,
// whose participations carry status + event only (fleet-fix #118). Event
// names, competitions and seasons come from the cached events collection.
export const ParticipationsDocument = graphql(/* GraphQL */ `
  query Participations($events: [ID!]!) {
    participations(filter: [{ events: $events }]) {
      id
      status
      startNumber
      pit
      teamName
      cups
      noDriverless
      eventClass {
        classes
      }
      event {
        id
      }
      vehicle {
        id
        vehicleName
        team {
          id
          name
          class
          # src/core/teamDisplay.ts (ui-conventions.md #98).
          university {
            id
            name
            nameShort
            nameInternal
            city
            country {
              iso2
            }
          }
        }
      }
    }
  }
`)

// The participation panel (ParticipationDetailPanel.vue): exactly the
// Participation surface core exposes, addressed by id through
// ParticipationFilter.ids (the schema has no participation(id) lookup). The
// roster selection is ParticipationMembers' own, so MemberEntry renders it
// unchanged; it is clamped server-side to the viewer's person-read scope and
// the panel renders what arrives. `mediaStory` is read for its presence only.
export const ParticipationDetailDocument = graphql(/* GraphQL */ `
  query ParticipationDetail($id: ID!) {
    participations(filter: [{ ids: [$id] }]) {
      id
      status
      startNumber
      pit
      teamName
      cups
      noDriverless
      mediaStory
      mediaStoryPublic
      eventClass {
        classes
        cups
      }
      event {
        id
        name
        season
        competition {
          id
          name
          tenant {
            id
            name
          }
        }
      }
      vehicle {
        id
        season
        vehicleName
        team {
          id
          name
          class
          university {
            id
            name
            nameShort
            nameInternal
            city
            country {
              iso2
            }
          }
        }
      }
      accreditations {
        id
        person {
          id
          givenName
          familyName
        }
        slotKind
        state
        eso
        asr
        paymentStatus
        designatedAt
        selections
        eligibility {
          selections
          teamMemberBadge
          ticket
          requirements {
            type {
              slug
              name
              requiresReview
              versionBased
              isPii
            }
            requiredFor
            satisfied
            blocksPrint
          }
        }
        ticket
      }
    }
  }
`)
